import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GET, POST, httpOk } from 'helpers/api'

// AI-помощник партнёра: свои клиенты (подписки, продажи, диагностика) и обучение
// по документации. Каких клиентов видит помощник, решает сервер по токену —
// здесь только чат.

const HINTS = [
	{
		title: 'partner_ai_hint_clients',
		items: ['partner_ai_q_debtors', 'partner_ai_q_unpaid_modules', 'partner_ai_q_client_pays'],
	},
	{
		title: 'partner_ai_hint_client',
		items: ['partner_ai_q_sales', 'partner_ai_q_yandex', 'partner_ai_q_sync'],
	},
	{
		title: 'partner_ai_hint_learning',
		items: ['partner_ai_q_bot', 'partner_ai_q_precheck', 'partner_ai_q_report'],
	},
]

function Index() {
	const { t } = useTranslation()

	const [messages, setMessages] = useState([])
	const [chatId, setChatId] = useState(null)
	const [client, setClient] = useState(null)
	const [question, setQuestion] = useState('')
	const [loading, setLoading] = useState(false)
	const [limitLeft, setLimitLeft] = useState(null)

	const messagesEndRef = useRef(null)

	async function getLimits() {
		const response = await GET('/partner/ai-assistant/limits', {}, { loader: false })
		if (httpOk(response)) setLimitLeft(response.data.left)
	}

	async function ask(text) {
		const value = (text ?? question).trim()
		if (!value || loading) return

		setMessages(prev => [...prev, { role: 'user', content: value }])
		setQuestion('')
		setLoading(true)

		const response = await POST('/partner/ai-assistant', {
			question: value,
			chat_id: chatId,
			lang: localStorage.getItem('lang') ?? 'ru',
		}, { notification: false })

		setLoading(false)

		if (httpOk(response)) {
			setChatId(response.data.chat_id)
			setClient(response.data.client_login)
			setLimitLeft(response.data.limit_left)
			setMessages(prev => [...prev, { role: 'assistant', content: response.data.answer }])
		} else {
			setMessages(prev => [...prev, {
				role: 'assistant',
				content: response?.data?.message ?? t('partner_ai_failed'),
				failed: true,
			}])
		}
	}

	function handleKeyDown(e) {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			ask()
		}
	}

	// Ссылки на документацию в ответе делаем кликабельными
	function renderContent(content) {
		return String(content ?? '').split(/(https:\/\/doc\.mison\.uz\/[^\s)]*)/g).map((part, index) => (
			part.startsWith('https://doc.mison.uz/')
				? <a href={part} target="_blank" rel="noreferrer" key={index}>{part}</a>
				: <React.Fragment key={index}>{part}</React.Fragment>
		))
	}

	useEffect(() => {
		getLimits()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
	}, [messages, loading])

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex align-items-center gap-2">
					<h4 className="vertical-center mb-0">{t('ai_assistant')}</h4>
					{client &&
						<span className="badge bg-primary">{client}</span>
					}
				</div>

				<div className="d-flex align-items-center gap-2">
					{limitLeft !== null &&
						<span className="text-muted small">{t('partner_ai_limit_left')}: {limitLeft}</span>
					}
					<button className="btn btn-primary"
						onClick={() => { setChatId(null); setMessages([]); setClient(null) }}>
						{t('partner_ai_new_chat')}
					</button>
				</div>
			</div>

			<div className="row" style={{ height: 'calc(100vh - 120px)' }}>
				<div className="col-md-12 h-100">
					<div className="card h-100 mb-0">
						<div className="card-body d-flex flex-column" style={{ minHeight: 0 }}>

							<div className="flex-grow-1 mb-3" style={{ overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
								{messages.length === 0 && !loading &&
									<div>
										<div className="alert alert-info">{t('partner_ai_intro')}</div>

										<div className="row">
											{HINTS.map((group, index) => (
												<div className="col-md-4 mb-3" key={index}>
													<div className="border rounded p-3 h-100">
														<div className="fw-bold mb-2">{t(group.title)}</div>
														{group.items.map((item, key) => (
															<button type="button"
																className="btn btn-outline-secondary btn-sm me-2 mb-2 text-start"
																key={key}
																onClick={() => setQuestion(t(item) + ' ')}>
																{t(item)}
															</button>
														))}
													</div>
												</div>
											))}
										</div>
									</div>
								}

								{messages.map((item, index) => (
									<div className={`d-flex mb-3 ${item.role === 'user' ? 'justify-content-end' : ''}`} key={index}>
										<div className={`p-3 rounded ${item.role === 'user' ? 'bg-primary text-white' : 'bg-light'}`}
											style={{ maxWidth: '80%', whiteSpace: 'pre-wrap' }}>
											{item.role === 'assistant' ? renderContent(item.content) : item.content}
										</div>
									</div>
								))}

								{loading &&
									<div className="d-flex mb-3">
										<div className="p-3 rounded bg-light text-muted">
											<i className="uil-spinner-alt"></i> {t('partner_ai_thinking')}
										</div>
									</div>
								}

								<div ref={messagesEndRef}></div>
							</div>

							<div className="d-flex gap-2">
								<textarea className="form-control" rows="2"
									placeholder={client ? t('partner_ai_placeholder_client') + ' ' + client : t('partner_ai_placeholder')}
									value={question}
									disabled={loading}
									onKeyDown={handleKeyDown}
									onChange={(e) => setQuestion(e.target.value)}>
								</textarea>

								<button className="btn btn-primary"
									disabled={loading || !question.trim()}
									onClick={() => ask()}>
									<i className="uil-message"></i>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default Index
